import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import "./Sidebar.scss";
import {
  fetchCollections,
  EXCLUDED_COLLECTIONS,
} from "../../Features/Market/Market";
import {
  parseSearch,
  buildSearch,
} from "../../Features/Category/categoryUrlParams";

export const SidebarDescription = ({
  activeCollection,
  isDescriptionExpanded,
  onToggleExpanded,
  fullDescription,
}) => {
  if (!activeCollection) return null;

  const text = fullDescription || "No description available";
  const isLong = text.length > 300;

  return (
    <div className="sidebar-description">
      <p
        className={`sidebar-description-text ${
          isDescriptionExpanded ? "expanded" : ""
        }`}
      >
        {text}
      </p>

      {isLong && (
        <span
          className="sidebar-description-toggle"
          onClick={() => onToggleExpanded(!isDescriptionExpanded)}
        >
          {isDescriptionExpanded ? "See Less" : "Read More"}
        </span>
      )}
    </div>
  );
};

const Sidebar = ({
  route = "/reproduction/collections",
  title = "Historical Reproduction",
  subtitle = "Collections",
  countryName = "",
  categories = null,
  onActiveCollectionChange = null,
  traits = null,
  selectedTrait = null,
  onTraitSelect = null,
  heroCollapsed = false,
  style = {},
  onDescriptionStateChange = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(!categories);
  const [activeCollection, setActiveCollection] = useState(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const handleClick = useCallback(
    (cat) => {
      setActiveCollection(cat);

      const search = buildSearch({
        country: countryName,
        collection: cat.id,
      });

      navigate(
        { pathname: route, search },
        {
          state: {
            country: countryName,
            collectionId: cat.id,
          },
        },
      );

      setIsOpen(false);
    },
    [navigate, route, countryName],
  );

  useEffect(() => {
    const { collection: collectionFromUrl } = parseSearch(location.search);

    const loadCollectionsData = async () => {
      if (categories && categories.length > 0) {
        const visibleCategories = categories.filter(
          (c) => !EXCLUDED_COLLECTIONS.includes(c.id?.toLowerCase()),
        );
        setCollections(visibleCategories);

        let currentCollection = null;

        if (collectionFromUrl) {
          currentCollection = visibleCategories.find(
            (c) =>
              c.id?.toLowerCase() === collectionFromUrl ||
              (c.address && c.address.toLowerCase() === collectionFromUrl),
          );
        }

        setActiveCollection(currentCollection || null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = (await fetchCollections()).filter(
          (c) => !EXCLUDED_COLLECTIONS.includes(c.id?.toLowerCase()),
        );
        setCollections(data);

        let currentCollection = null;

        if (collectionFromUrl) {
          currentCollection = data.find(
            (c) =>
              c.id?.toLowerCase() === collectionFromUrl ||
              (c.address && c.address.toLowerCase() === collectionFromUrl),
          );
        }

        setActiveCollection(currentCollection || null);
      } catch (error) {
        console.error("Error loading collections:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCollectionsData();
  }, [categories, location.search]);

  useEffect(() => {
    if (activeCollection && onActiveCollectionChange) {
      onActiveCollectionChange(activeCollection);
    }
  }, [activeCollection, onActiveCollectionChange]);

  useEffect(() => {
    setIsDescriptionExpanded(false);
  }, [activeCollection]);

  const COLLAPSED_CHAR_LIMIT = 260;

  const fullDescription =
    activeCollection?.description || "No description available";

  const shouldTruncate = fullDescription.length > COLLAPSED_CHAR_LIMIT;

  const collapsedDescription = shouldTruncate
    ? fullDescription.slice(0, COLLAPSED_CHAR_LIMIT).trimEnd()
    : fullDescription;

  useEffect(() => {
    if (!onDescriptionStateChange) return;

    onDescriptionStateChange({
      activeCollection,
      isDescriptionExpanded,
      setIsDescriptionExpanded,
      fullDescription,
      collapsedDescription,
      shouldTruncate,
    });
  }, [
    onDescriptionStateChange,
    activeCollection,
    isDescriptionExpanded,
    fullDescription,
    collapsedDescription,
    shouldTruncate,
  ]);

  return (
    <>
      <aside
        className={`sidebar ${isOpen ? "open" : ""} ${
          heroCollapsed ? "sidebar--hero-collapsed" : ""
        }`}
        style={style}
      >
        <div className="sidebar-container">
          <h2>
            {countryName && activeCollection
              ? `${countryName} - ${activeCollection.name}`
              : title}
          </h2>

          {loading ? (
            <p>Loading collections...</p>
          ) : collections.length === 0 ? (
            <p>No collections found</p>
          ) : (
            <>
              {activeCollection && (
                <SidebarDescription
                  activeCollection={activeCollection}
                  isDescriptionExpanded={isDescriptionExpanded}
                  onToggleExpanded={setIsDescriptionExpanded}
                  fullDescription={fullDescription}
                />
              )}

              <hr className="sidebar-hr" />
              <h3 className="sidebar-subtitle">{subtitle}</h3>

              {traits && traits.length > 0 ? (
                <ul className="sidebar-ul sidebar-traits">
                  {traits.map((trait) => (
                    <li
                      key={trait.name}
                      onClick={() => onTraitSelect?.(trait.name)}
                      className={`sidebar-item ${
                        selectedTrait === trait.name ? "active" : ""
                      }`}
                    >
                      {trait.name} ({trait.count})
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="sidebar-ul">
                  {collections.map((cat) => (
                    <li
                      key={cat.id || cat.name}
                      onClick={() => handleClick(cat)}
                      className={`sidebar-item ${
                        activeCollection?.id === cat.id ? "active" : ""
                      }`}
                    >
                      {cat.name} ({cat.count || 0})
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <NavLink to={route.replace("/collections", "")} className="back-btn2">
          ◀ Back
        </NavLink>
      </aside>
    </>
  );
};

export default React.memo(Sidebar);
